import { AnchorHTMLAttributes, ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-accent text-legal-900 font-semibold hover:bg-accent-400 shadow-elevation-1 hover:shadow-glow-accent',
  secondary:
    'bg-white text-legal-700 border border-legal-300 hover:border-legal-400 hover:bg-legal-50',
  ghost: 'text-legal-500 hover:text-legal-800 hover:bg-legal-100',
  danger: 'bg-red-600 text-white hover:bg-red-500',
};

// md/lg meet the 44px touch-target minimum; sm is for dense desktop rows only.
const SIZES: Record<Size, string> = {
  sm: 'text-xs px-3 py-1.5 rounded-lg gap-1.5',
  md: 'text-sm px-4 py-2.5 min-h-[44px] rounded-lg gap-2',
  lg: 'text-base px-6 py-3 min-h-[48px] rounded-lg gap-2',
};

const BASE =
  'inline-flex items-center justify-center font-medium transition-all duration-150 press disabled:opacity-60 disabled:pointer-events-none whitespace-nowrap';

interface CommonProps {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
  children: ReactNode;
  className?: string;
}

type ButtonAsButton = CommonProps &
  ButtonHTMLAttributes<HTMLButtonElement> & { to?: undefined; href?: undefined };
type ButtonAsLink = CommonProps & Omit<LinkProps, 'className'> & { href?: undefined };
type ButtonAsAnchor = CommonProps &
  AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; to?: undefined };

export type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

/**
 * The one button. Renders a real <button>, <Link> or <a> (never a styled div)
 * so the analytics delegated click listener keeps working; `data-track` and
 * all native props pass straight through.
 */
export const Button = forwardRef<HTMLElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', isLoading, children, className, ...rest },
  ref
) {
  const cls = twMerge(clsx(BASE, VARIANTS[variant], SIZES[size]), className);
  const content = (
    <>
      {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </>
  );
  if ('to' in rest && rest.to !== undefined) {
    return (
      <Link ref={ref as never} className={cls} {...(rest as LinkProps)}>
        {content}
      </Link>
    );
  }
  if ('href' in rest && rest.href !== undefined) {
    return (
      <a ref={ref as never} className={cls} {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {content}
      </a>
    );
  }
  return (
    <button
      ref={ref as never}
      className={cls}
      disabled={isLoading || (rest as ButtonHTMLAttributes<HTMLButtonElement>).disabled}
      {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {content}
    </button>
  );
});
